import type { PayPalButtonsComponentProps } from "@paypal/react-paypal-js";
import { useMutation } from "@apollo/client";
import { lastTouchInput } from "@/shared/utils/attribution";
import {
  PaymentProviderType,
  type CompletePaymentMutationType,
  type CompletePaymentMutationVariablesType,
  type CreatePaymentMutationType,
  type CreatePaymentMutationVariablesType,
  type CreateProviderPaymentMutationType,
  type CreateProviderPaymentMutationVariablesType,
} from "@/types/graphql/site.generated";

// Логика PayPalCheckoutButton вынесена в отдельный файл без хуков и JSX
// (по образцу terminalStatus.ts рядом с PaymentStatusStep.tsx): тесты
// проверяют разбор ответа createOrder/completePayment напрямую, без
// рендера React-дерева и без реального @paypal/react-paypal-js.

// PayPal локализует кнопку и попап этим кодом. SUPPORTED_LOCALES витрины
// ("en"/"ru"/"pl"/"pt") мельче формата PayPal (язык_СТРАНА), отображение
// один в один, как в легаси (getPaypalLocale).
const PAYPAL_DEFAULT_LOCALE = "en_US";
const PAYPAL_LOCALES: Record<string, string> = {
  en: PAYPAL_DEFAULT_LOCALE,
  ru: "ru_RU",
  pl: "pl_PL",
  pt: "pt_PT",
};

export function getPaypalLocale(language: string): string {
  return PAYPAL_LOCALES[language] ?? PAYPAL_DEFAULT_LOCALE;
}

// @paypal/react-paypal-js не экспортирует OnApproveData/OnApproveActions из
// публичного индекса напрямую (они объявлены в @paypal/paypal-js, не
// прописанном у нас прямой зависимостью): типы кнопки уже несут их в
// сигнатуре onApprove, достаём оттуда, а не тянем ещё один пакет.
type PayPalOnApprove = NonNullable<PayPalButtonsComponentProps["onApprove"]>;
export type OnApproveData = Parameters<PayPalOnApprove>[0];
export type OnApproveActions = Parameters<PayPalOnApprove>[1];

// Ответ createProviderPayment для PAYPAL (спека §5.1/§6): ордер уже заведён
// на сервере, сумма в браузер не уходит, JS SDK получает только его id.
interface PayPalProviderPaymentResult {
  paypalOrderId: string;
  clientId: string;
  currency: string;
}

// Тело ошибки PayPal (см. PaymentService.toPayPalCompleteError): либо
// разобранный ответ вендора (debug_id, details[].issue), либо { message }
// при сетевом сбое.
interface PayPalCompleteError {
  debug_id?: string;
  details?: { issue?: string; description?: string }[];
}

export type CreatePaymentFn = ReturnType<
  typeof useMutation<
    CreatePaymentMutationType,
    CreatePaymentMutationVariablesType
  >
>[0];
export type CreateProviderPaymentFn = ReturnType<
  typeof useMutation<
    CreateProviderPaymentMutationType,
    CreateProviderPaymentMutationVariablesType
  >
>[0];
export type CompletePaymentFn = ReturnType<
  typeof useMutation<
    CompletePaymentMutationType,
    CompletePaymentMutationVariablesType
  >
>[0];

interface CreatePayPalOrderDeps {
  createPayment: CreatePaymentFn;
  createProviderPayment: CreateProviderPaymentFn;
  productId: string;
  priceToken: string;
  recurrent: boolean;
  setPaymentId: (id: string) => void;
  errorMessage: string;
}

// Заводит платёж под явный provider: PAYPAL и заводит сам ордер у
// провайдера (спека §6): вызывается SDK по клику на кнопку, а не при
// открытии диалога.
export function createPayPalOrder(deps: CreatePayPalOrderDeps) {
  return async (): Promise<string> => {
    const { data: paymentData } = await deps.createPayment({
      variables: {
        productId: deps.productId,
        priceToken: deps.priceToken,
        recurrent: deps.recurrent,
        provider: PaymentProviderType.PaypalType,
        utm: lastTouchInput(),
      },
    });
    const id = paymentData?.createPayment?.id;
    if (!id) throw new Error(deps.errorMessage);

    const { data: providerData } = await deps.createProviderPayment({
      variables: { id },
    });
    const result = providerData?.createProviderPayment as
      | PayPalProviderPaymentResult
      | undefined;
    if (!result?.paypalOrderId) throw new Error(deps.errorMessage);

    deps.setPaymentId(id);
    return result.paypalOrderId;
  };
}

interface ApprovePayPalOrderDeps {
  completePayment: CompletePaymentFn;
  getPaymentId: () => string | null;
  onApproved: (paymentId: string) => void;
  setError: (message: string | null) => void;
  errorMessage: string;
}

// Разбор ответа completePayment взят из боевого легаси (спека §6): успех
// уводит к опросу статуса; туда же уходит принятый, но ещё не завершённый
// захват (PENDING без ошибки); отклонённое платёжное средство
// перезапускает оплату средствами самого SDK (actions.restart, ордер
// переиспользуется); прочая ошибка показывается покупателю текстом вместе
// с debug_id.
export function approvePayPalOrder(deps: ApprovePayPalOrderDeps) {
  return async (
    data: OnApproveData,
    actions: OnApproveActions,
  ): Promise<void> => {
    const paymentId = deps.getPaymentId();
    if (!paymentId) return;
    deps.setError(null);

    const { data: completeData } = await deps.completePayment({
      variables: {
        id: paymentId,
        input: { paypalOrderId: data.orderID },
      },
    });
    const payment = completeData?.completePayment;

    if (payment?.status === "SUCCEEDED") {
      deps.onApproved(paymentId);
      return;
    }

    const completeError = payment?.completeError as
      | PayPalCompleteError
      | undefined;

    // Захват принят, но ещё не завершён: у PayPal штатно бывает ордер
    // COMPLETED с захватом PENDING (оплата с банковского счёта, ручная
    // проверка), сервер в этом случае оставляет платёж PENDING без ошибки
    // и ждёт вебхука. Уводим в опрос статуса, как страница возврата Paynet:
    // опрос сам закончится на SUCCEEDED или на CANCELED (markFailed пишет
    // именно CANCELED), а пока показывается нейтральный текст ожидания.
    // Показать здесь ошибку было бы опаснее всего: покупатель, у которого
    // деньги в обработке, заплатил бы ещё раз, и переоткрытый диалог завёл
    // бы второй платёж со вторым ордером (покупки ещё нет, гейт «уже
    // куплено» не срабатывает), то есть два списания за один продукт.
    if (payment?.status === "PENDING" && !completeError) {
      deps.onApproved(paymentId);
      return;
    }

    const issue = completeError?.details?.[0]?.issue;
    if (issue === "INSTRUMENT_DECLINED") {
      actions.restart();
      return;
    }

    deps.setError(
      completeError?.debug_id
        ? `${deps.errorMessage}: ${completeError.debug_id}`
        : deps.errorMessage,
    );
  };
}
